import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import LandingPage from "@/pages/LandingPage";
import BrowseTradies from "@/pages/BrowseTradies";
import TradieProfile from "@/pages/TradieProfile";
import GetQuote from "@/pages/GetQuote";
import TradieSignup from "@/pages/TradieSignup";
import AdminPanel from "@/pages/AdminPanel";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import CookiePolicy from "@/pages/CookiePolicy";
import LocationPage from "@/pages/LocationPage";
import ServicePage from "@/pages/ServicePage";
import BuyCredits from "@/pages/BuyCredits";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Pricing from "@/pages/Pricing";
import { Toaster } from "@/components/ui/sonner";

// Australian Hotspots Directory Pages
import { HotspotsHome, CityPage, CategoryPage, BusinessPage, AdvertisePage, ListBusinessPage } from "@/pages/hotspots";

// My Boy Lollipop - AI Male Model Subscription Landing
import MyBoyLollipop from "@/pages/MyBoyLollipop";
import LollipopAdmin from "@/pages/LollipopAdmin";

// SEO Furnace - Mass Page Generator
import SEOFurnace from "@/pages/SEOFurnace";

// Coffee On The Go - Mobile Coffee Van
import CoffeeOnTheGo from "@/pages/CoffeeOnTheGo";
import CoffeeOnTheGoAdmin from "@/pages/CoffeeOnTheGoAdmin";

// Puppoto - Dog Social Platform
import Puppoto from "@/pages/Puppoto";
import PupMatch from "@/pages/PupMatch";
import PuppotoProfile from "@/pages/PuppotoProfile";
import PuppotoAuthCallback from "@/pages/PuppotoAuthCallback";
import PupSocial from "@/pages/PupSocial";
import PupMessages from "@/pages/PupMessages";
import PupAdoptions from "@/pages/PupAdoptions";
import PupShows from "@/pages/PupShows";
import PupSponsors from "@/pages/PupSponsors";
import PupSalesDashboard from "@/pages/PupSalesDashboard";
import PupJoinSales from "@/pages/PupJoinSales";
import PupSalesAdmin from "@/pages/PupSalesAdmin";
import PupTeam from "@/pages/PupTeam";
import PupRainbowBridge from "@/pages/PupRainbowBridge";
import PupCelebrations from "@/pages/PupCelebrations";
import PupDirectory from "@/pages/PupDirectory";
import PupDirectorySubmit from "@/pages/PupDirectorySubmit";
import PupDirectoryAdmin from "@/pages/PupDirectoryAdmin";
import PuppotoPartnersInvite from "@/pages/PuppotoPartnersInvite";
import PuppotoJoin from "@/pages/PuppotoJoin";
import PuppotoBusinessCards from "@/pages/PuppotoBusinessCards";
import PuppotoComingSoon from "@/pages/PuppotoComingSoon";

// Puppoto Launch Gate - shows Coming Soon until May 1st 2026 AEST or if admin logged in
const PuppotoGate = ({ children }) => {
  const launchDate = new Date('2026-05-01T00:00:00+10:00').getTime();
  const now = new Date().getTime();
  const isLaunched = now >= launchDate;
  const hasAdminAccess = sessionStorage.getItem('puppoto_admin_access') === 'true';
  
  if (isLaunched || hasAdminAccess) {
    return children;
  }
  return <PuppotoComingSoon />;
};

// Kata AI Search Concierge
import KataChat from "@/components/KataChat";

// Wrapper to conditionally show Kata on Hotspots pages
const KataWrapper = () => {
  const location = useLocation();
  const isHotspotsPage = location.pathname.startsWith('/hotspots');
  return isHotspotsPage ? <KataChat /> : null;
};

// Domain-aware default page component
const DomainAwareHome = () => {
  const hostname = window.location.hostname;
  
  // If on seofurnace.com.au, show SEO Furnace
  if (hostname.includes('seofurnace')) {
    return <SEOFurnace />;
  }
  
  // If on puppoto.com, show Puppoto (with launch gate)
  if (hostname.includes('puppoto')) {
    const launchDate = new Date('2026-04-01T00:00:00+11:00').getTime();
    const now = new Date().getTime();
    const isLaunched = now >= launchDate;
    const hasAdminAccess = sessionStorage.getItem('puppoto_admin_access') === 'true';
    
    if (isLaunched || hasAdminAccess) {
      return <Puppoto />;
    }
    return <PuppotoComingSoon />;
  }
  
  // Default to LandingPage (Tradies) for other domains
  return <LandingPage />;
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<DomainAwareHome />} />
            <Route path="/browse" element={<BrowseTradies />} />
            {/* SEO-friendly tradie URLs */}
            <Route path="/tradies/:location/:service/:slug" element={<TradieProfile />} />
            {/* Legacy UUID URLs - still supported */}
            <Route path="/tradie/:id" element={<TradieProfile />} />
            <Route path="/get-quote" element={<GetQuote />} />
            <Route path="/join" element={<TradieSignup />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/buy-credits" element={<BuyCredits />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            {/* Service Index Page - e.g., /services/electrician */}
            <Route path="/services/:serviceSlug" element={<ServicePage />} />
            
            {/* ========== AUSTRALIAN HOTSPOTS DIRECTORY ========== */}
            <Route path="/hotspots" element={<HotspotsHome />} />
            <Route path="/hotspots/advertise" element={<AdvertisePage />} />
            <Route path="/hotspots/list-business" element={<ListBusinessPage />} />
            <Route path="/hotspots/:cityId" element={<CityPage />} />
            <Route path="/hotspots/:cityId/:categorySlug" element={<CategoryPage />} />
            <Route path="/hotspots/:cityId/:categorySlug/:subLocation" element={<CategoryPage />} />
            {/* SEO-friendly business URLs */}
            <Route path="/hotspots/:cityId/:categorySlug/business/:slug" element={<BusinessPage />} />
            {/* Legacy UUID URLs - still supported */}
            <Route path="/hotspots/business/:businessId" element={<BusinessPage />} />
            <Route path="/hotspots/category/:categorySlug" element={<CategoryPage />} />
            
            {/* ========== MY BOY LOLLIPOP - AI MODEL SITE ========== */}
            <Route path="/myboylollipop" element={<MyBoyLollipop />} />
            <Route path="/lollipop" element={<MyBoyLollipop />} />
            <Route path="/lollipop/admin" element={<LollipopAdmin />} />
            
            {/* ========== SEO FURNACE - MASS PAGE GENERATOR ========== */}
            <Route path="/seofurnace" element={<SEOFurnace />} />
            <Route path="/furnace" element={<SEOFurnace />} />
            
            {/* ========== COFFEE ON THE GO - MOBILE COFFEE VAN ========== */}
            <Route path="/coffeeonthego" element={<CoffeeOnTheGo />} />
            <Route path="/coffeeonthego/admin" element={<CoffeeOnTheGoAdmin />} />
            
            {/* ========== PUPPOTO - DOG SOCIAL PLATFORM ========== */}
            <Route path="/puppoto" element={<PuppotoGate><Puppoto /></PuppotoGate>} />
            <Route path="/puppoto/match" element={<PuppotoGate><PupMatch /></PuppotoGate>} />
            <Route path="/puppoto/profile" element={<PuppotoGate><PuppotoProfile /></PuppotoGate>} />
            <Route path="/puppoto/auth/callback" element={<PuppotoAuthCallback />} />
            <Route path="/puppoto/social" element={<PuppotoGate><PupSocial /></PuppotoGate>} />
            <Route path="/puppoto/messages" element={<PuppotoGate><PupMessages /></PuppotoGate>} />
            <Route path="/puppoto/messages/:conversationId" element={<PuppotoGate><PupMessages /></PuppotoGate>} />
            <Route path="/puppoto/adopt" element={<PuppotoGate><PupAdoptions /></PuppotoGate>} />
            <Route path="/puppoto/shows" element={<PuppotoGate><PupShows /></PuppotoGate>} />
            <Route path="/puppoto/sponsors" element={<PuppotoGate><PupSponsors /></PuppotoGate>} />
            <Route path="/puppoto/partners" element={<PuppotoGate><PupSponsors /></PuppotoGate>} />
            <Route path="/puppoto/sales-dashboard" element={<PuppotoGate><PupSalesDashboard /></PuppotoGate>} />
            <Route path="/puppoto/join-sales" element={<PuppotoGate><PupJoinSales /></PuppotoGate>} />
            <Route path="/puppoto/sales-admin" element={<PuppotoGate><PupSalesAdmin /></PuppotoGate>} />
            <Route path="/puppoto/team" element={<PuppotoGate><PupTeam /></PuppotoGate>} />
            <Route path="/puppoto/rainbow-bridge" element={<PuppotoGate><PupRainbowBridge /></PuppotoGate>} />
            <Route path="/puppoto/celebrations" element={<PuppotoGate><PupCelebrations /></PuppotoGate>} />
            <Route path="/puppoto/directory" element={<PuppotoGate><PupDirectory /></PuppotoGate>} />
            <Route path="/puppoto/directory/submit" element={<PuppotoGate><PupDirectorySubmit /></PuppotoGate>} />
            <Route path="/puppoto/directory/admin" element={<PuppotoGate><PupDirectoryAdmin /></PuppotoGate>} />
            {/* QR Code Landing Pages - No gate, always accessible */}
            <Route path="/puppoto/partners-invite" element={<PuppotoPartnersInvite />} />
            <Route path="/puppoto/join" element={<PuppotoJoin />} />
            <Route path="/puppoto/business-cards" element={<PuppotoBusinessCards />} />
            {/* Coming Soon page accessible directly */}
            <Route path="/puppoto/coming-soon" element={<PuppotoComingSoon />} />
            
            {/* SEO Location Pages - MUST BE LAST (catch-all pattern) */}
            <Route path="/:serviceSlug/:locationSlug" element={<LocationPage />} />
          </Routes>
          {/* Kata AI Chat - Hotspots Pages Only */}
          <KataWrapper />
        </BrowserRouter>
        <Toaster />
      </div>
    </HelmetProvider>
  );
}

export default App;